// The BLE Test screen's batch panel — one command to every TICKED device, one
// device at a time (core/ble/bleBatch). Shown under the scan list while the
// list is in "Select" mode.
//
//   [Command ▾]  (Wi-Fi name / password, or the custom text)  [Run on N]  [Stop]
//   ✓ D4F98D14B0A0   0,0#BLE
//   ✗ D4F98D123E30   Sent — no answer

import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, LinearProgress, MenuItem, TextField, Typography, useTheme,
} from '@mui/material';
import { BATCH_COMMANDS, batchCommand, runBleBatch } from '../../../core/ble/bleBatch';

const FIELD_LABEL = { ssid: 'Wi-Fi name', pass: 'Password', text: 'Command' };

export default function BleBatchPanel({ selected, onRunning }) {
  const theme = useTheme();
  const c = theme.sli;
  const [key, setKey] = useState('allon');
  const [fields, setFields] = useState({ ssid: '', pass: '', text: '' });
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const stopRef = useRef(false);

  const cmd = batchCommand(key);
  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));
  const fieldSx = {
    '& .MuiInputBase-input': { color: c.text },
    '& .MuiInputLabel-root': { color: `${c.text}B3` },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: `${c.text}66` },
    '& .MuiSvgIcon-root': { color: c.text },
  };

  const run = async () => {
    setError('');
    const built = cmd.build(fields);
    if (!built.ok) { setError(built.error); return; }
    stopRef.current = false;
    setRunning(true);
    onRunning?.(true);
    setResults([]);
    try {
      await runBleBatch(selected, cmd, fields, {
        onProgress: setResults,
        shouldStop: () => stopRef.current,
      });
    } catch (e) {
      setError((e && e.message) || 'Batch failed');
    } finally {
      setRunning(false);
      onRunning?.(false);
    }
  };

  const done = results ? results.length : 0;
  const okCount = results ? results.filter((r) => r.ok).length : 0;

  return (
    <Box sx={{ border: `1px solid ${c.text}44`, borderRadius: '12px', p: 1.25, my: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select size="small" label="Command" value={key}
          onChange={(e) => { setKey(e.target.value); setError(''); }}
          sx={{ minWidth: 150, ...fieldSx }} disabled={running}
        >
          {BATCH_COMMANDS.map((b) => <MenuItem key={b.key} value={b.key}>{b.label}</MenuItem>)}
        </TextField>
        {cmd.fields.map((f) => (
          <TextField
            key={f} size="small" label={FIELD_LABEL[f]} value={fields[f]} onChange={set(f)}
            disabled={running} sx={{ flex: '1 1 130px', ...fieldSx }}
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          />
        ))}
      </Box>

      {cmd.reboots && (
        <Typography sx={{ color: `${c.text}99`, fontSize: 11, mt: 0.75 }}>
          A Wi-Fi change restarts each device — its link dropping after the command counts as done.
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
        <Button
          variant="contained" disabled={running || !selected.length} onClick={run}
          sx={{ bgcolor: c.button, color: '#fff', textTransform: 'none', fontWeight: 700 }}
        >
          {`${cmd.label} → ${selected.length} device${selected.length === 1 ? '' : 's'}`}
        </Button>
        {running && (
          <Button variant="outlined" color="error" onClick={() => { stopRef.current = true; }} sx={{ textTransform: 'none' }}>
            Stop
          </Button>
        )}
        {results && (
          <Typography sx={{ color: c.text, fontSize: 12, ml: 'auto' }}>
            {`${done}/${selected.length} · ${okCount} ok`}
          </Typography>
        )}
      </Box>
      {running && <LinearProgress sx={{ mt: 1 }} variant="determinate" value={(done / Math.max(1, selected.length)) * 100} />}

      {results && results.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {results.map((r) => (
            <Typography key={r.uniqueNumber} sx={{ fontSize: 12, fontFamily: 'monospace', color: r.ok ? '#81C784' : '#EF5350' }}>
              {`${r.ok ? '✓' : '✗'} ${r.uniqueNumber}  ${r.ok ? (r.reply || 'ok') : r.error}`}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
